import moment from 'moment';
import { parseJSON } from './json';
import { API_ENDPOINT } from '../constants';

/**
* Data access + period helpers for the "API analytics (all requests)" page:
* every request on the backend, from the `logs` collection. The analytics
* tabs read the hourly/daily summaries behind `/data/api-logs/overview`
* (fast even for a year); only the Logs tab reads raw logs, at most 24 h at
* a time.
*/

export const MAX_OVERVIEW_DAYS = 366;
export const MAX_LOGS_HOURS = 24;
export const DEFAULT_LOGS_ENTRIES = 50;
export const MAX_LOGS_ENTRIES = 100;
export const HEATMAP_TZ = 'Europe/Stockholm';

export const OVERVIEW_PERIODS = [
    { value: '24h', label: 'Last 24 hours', days: 1 },
    { value: '7d', label: 'Last 7 days', days: 7 },
    { value: '30d', label: 'Last 30 days', days: 30 },
    { value: '90d', label: 'Last 90 days', days: 90 },
    { value: '365d', label: 'Last 365 days', days: 365 },
    { value: 'custom', label: 'Custom' },
];

export const LOGS_PERIODS = [
    { value: '1h', label: '1 h', hours: 1 },
    { value: '6h', label: '6 h', hours: 6 },
    { value: '24h', label: '24 h', hours: 24 },
    { value: 'custom', label: 'Custom' },
];

export const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx'];
export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/**
* `{from, to, days}` as inclusive UTC `YYYY-MM-DD` days. "24 h" is today only
* (`from = to = today`), the summaries are per day/hour. Null for an
* incomplete custom range.
*/
export const overviewRange = ({ preset, customFrom, customTo }) => {
    if (preset === 'custom') {
        if (!customFrom || !customTo) return null;
        return { from: customFrom, to: customTo, days: moment.utc(customTo).diff(moment.utc(customFrom), 'days') + 1 };
    }

    let def = OVERVIEW_PERIODS.find((p) => p.value === preset) || OVERVIEW_PERIODS[2];
    let to = moment.utc();
    let from = moment.utc(to).subtract(def.days - 1, 'days');

    return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD'), days: def.days };
};

/** `{from, to, hours}` ISO strings for the Logs tab; null for an incomplete custom range */
export const logsRange = ({ preset, customFrom, customTo }) => {
    if (preset === 'custom') {
        if (!customFrom || !customTo) return null;
        let from = moment(customFrom);
        let to = moment(customTo);
        return { from: from.toISOString(), to: to.toISOString(), hours: to.diff(from, 'hours', true) };
    }

    let def = LOGS_PERIODS.find((p) => p.value === preset) || LOGS_PERIODS[0];
    let to = moment();

    return { from: moment(to).subtract(def.hours, 'hours').toISOString(), to: to.toISOString(), hours: def.hours };
};

export const statusClassOf = (status) => {
    let n = Number(status);
    return isNaN(n) ? null : `${Math.floor(n / 100)}xx`;
};

const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
});

const request = (url, fallbackError) => fetch(url, { method: 'GET', headers: authHeaders() })
    .then(parseJSON)
    .then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { data: result, error: null, status };
        }

        if (status === 404) {
            return { data: null, error: `The backend has no ${url.replace(API_ENDPOINT, '').split('?')[0]} route (404).`, status };
        }

        if (status === 401 || status === 403) {
            return { data: null, error: `No permission for ${url.replace(API_ENDPOINT, '').split('?')[0]} (${status}) - the route has not been added to a module yet.`, status };
        }

        return { data: null, error: result && result.error ? result.error : fallbackError, status };
    })
    .catch(() => ({ data: null, error: fallbackError, status: 0 }));

const query = (params) => {
    let qs = new URLSearchParams();
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') qs.set(key, params[key]);
    });
    let s = qs.toString();
    return s ? `?${s}` : '';
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchApiLogsOverview = ({ from, to, interval, tz = HEATMAP_TZ }) => (
    request(`${API_ENDPOINT}/data/api-logs/overview${query({ from, to, interval, tz })}`, 'Failed to load the overview')
);

/** current load: the last 5 minutes straight from the logs */
export const fetchApiLogsLive = () => request(`${API_ENDPOINT}/data/api-logs/live`, 'Failed to load the live load');

export const LIVE_REFRESH_MS = 15000;

/**
* Timeseries granularity the backend allows per period length; '' = let it
* pick (hourly up to 7 days, daily beyond).
*/
export const CHART_INTERVALS = [
    { value: 'minute', label: 'Minute', maxDays: 2 },
    { value: 'hour', label: 'Hour', maxDays: 31 },
    { value: 'day', label: 'Day', maxDays: MAX_OVERVIEW_DAYS },
];

export const isIntervalAllowed = (interval, days) => {
    let def = CHART_INTERVALS.find((i) => i.value === interval);
    return !!def && days <= def.maxDays;
};

/**
* A date in the page's time zone (Europe/Stockholm), whatever the viewer's
* own zone is - the peaks and the heatmap are all read in that zone.
*/
export const formatInTz = (at, { seconds = false, date = true } = {}) => {
    if (!at) return '—';
    let d = new Date(at);
    if (isNaN(d.getTime())) return '—';
    let parts = {};
    new Intl.DateTimeFormat('en-GB', {
        timeZone: HEATMAP_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
    let time = `${parts.hour}:${parts.minute}${seconds ? `:${parts.second}` : ''}`;
    return date ? `${parts.year}-${parts.month}-${parts.day} ${time}` : time;
};

const LOG_QUERY_KEYS = [
    'from', 'to', 'method', 'status', 'statusClass', 'minMs', 'endpoint', 'userAgent',
    'ip', 'username', 'requestId', 'route', 'minDbQueries', 'externalApi', 'replica',
    'sortField', 'sortType', 'page', 'entries',
];

/** @returns {Promise<{items: object[], total: number, error: string|null, rangeTooLong: boolean}>} */
export const fetchApiLogs = (filters) => {
    let params = {};
    LOG_QUERY_KEYS.forEach((key) => { params[key] = filters[key]; });

    return request(`${API_ENDPOINT}/data/api-logs/logs${query(params)}`, 'Failed to load logs').then(({ data, error, status }) => {
        if (error) {
            let rangeTooLong = status === 400 && /24 hours/i.test(error);
            return { items: [], total: 0, error, rangeTooLong };
        }
        return { items: (data && data.items) || [], total: (data && data.total) || 0, error: null, rangeTooLong: false };
    });
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchApiLog = (id) => request(`${API_ENDPOINT}/data/api-logs/logs/${id}`, 'Failed to load the log');
